import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    CcRequirementState,
    CcRequirementHistory,
    CcRequirementHistoryCreate,
} from "server/models"
import {
    IDLE, PENDING,
    WORKFLOW_LEVEL_ASSIGNED,
    WORKFLOW_RERUN,
} from "const"
import { StateType, CcStateSettingsType } from "client/utils"
import { ContentColumn } from "client/parts/layout"
import {
    StoreType,

    LoadCcRequirementRevisionData,
    UpdateCcRequirementVerifier,
    CreateManyCcRequirementHistory,

    initCcRequirementHistory,
    setCcRequirementSelectedRequirementIds,
    setCcRequirementReviewListLoadState,
} from "client/store"
import {
    Empty,
    SelectDropdown,
    SelectDropdownItem,
    UserIcon,
    Button,
    SpinnerIcon,
    MoveTableBottom,
} from "client/components"

import InputSelectAll from "./selectAll"

const Bottom = () => {
    const tableClass = useSelector((state: StoreType) => state.ccRequirement.tableClass)
    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccRequirement.revisionActiveList)
    const requirementStateList = useSelector((state: StoreType) => state.ccRequirement.requirementStateList)
    const requirementHistoryList = useSelector((state: StoreType) => state.ccRequirement.requirementHistoryList)

    const selectedRequirementIds = useSelector((state: StoreType) => state.ccRequirement.selectedRequirementIds)
    const visibleRequirementIds = useSelector((state: StoreType) => state.ccRequirement.visibleRequirementIds)
    const visibleSelected = selectedRequirementIds.filter((id, index) => (
        selectedRequirementIds.indexOf(id) === index // remove duplicates
        && visibleRequirementIds.includes(id)
    ))

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[3] || []

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [selectedLevelId, setSelectedLevelId] = useState<number>(null)
    const [selectedUserId, setSelectedUserId] = useState<number>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    const table = document.getElementsByClassName(tableClass)[0] as HTMLElement
    const scrollableParent = document.body.querySelector(ContentColumn)

    useEffect(() => {
        fixBottomOnScroll(null, true)

        scrollableParent.addEventListener("scroll", fixBottomOnScroll)
        return () => scrollableParent.removeEventListener("scroll", fixBottomOnScroll)
    }, [visibleSelected])

    const fixBottomOnScroll = (e?: MouseEvent, forceDisplay?: boolean) => {
        if (!table) return
        if (visibleSelected.length > 0) {
            const windowHeight = window.innerHeight
            const tableBounding = table.getBoundingClientRect()
            const tableHeight = tableBounding.height
            const offsetTop = tableBounding.top
            const diff = windowHeight - (tableHeight + offsetTop)

            if (diff > 50) { // when table is scrolled to down move its bottom to its place
                MoveTableBottom(table, false)
            } else if (diff < -50) { // when table starts to scroll from bottom to up
                MoveTableBottom(table, true)
            } else if (forceDisplay) {
                MoveTableBottom(table, true)
            }
        } else {
            MoveTableBottom(table, false)
        }
    }

    const changeLevel = (levelId: number) => {
        setSelectedLevelId(levelId)
        setSelectedUserId(null)
    }

    const saveAssigned = () => {
        const stateArray: CcRequirementState[] = []
        const historyArray: CcRequirementHistoryCreate[] = []
        const promiseArray: Promise<any>[] = []

        visibleSelected.forEach((requirementId) => {
            const requirementState = requirementStateList[requirementId] || null
            const requirementHistory = requirementHistoryList[requirementId] || []
            const revision = revisionActiveList[requirementId]

            let lastHistory: CcRequirementHistory = null

            // get the last history row made by user, not by 'system'
            for (let i = requirementHistory.length - 1; i >= 0; i--) {
                const history = requirementHistory[i]
                if (history.workflow === WORKFLOW_RERUN) {
                    break
                }
                if (history.systemMessage === false) {
                    lastHistory = history
                    break
                }
            }

            const settings: CcStateSettingsType = requirementState && requirementState.settings
                ? JSON.parse(requirementState.settings)
                : {}
            const prevUserId = settings[selectedLevelId]

            if (prevUserId !== selectedUserId) {
                settings[selectedLevelId] = selectedUserId

                const firstLevelId = levelIds.length ? levelIds[0] : null
                const nextLevelId = (requirementState && requirementState.levelId) || firstLevelId
                const nextVerifierId = (requirementState && requirementState.verifierId) || null

                stateArray.push({
                    requirementId,
                    requirementRevisionId: revision.id,
                    levelId: nextLevelId,
                    verifierId: nextLevelId === selectedLevelId ? selectedUserId : nextVerifierId,
                    settings: JSON.stringify(settings),
                })
                historyArray.push({
                    ...initCcRequirementHistory,
                    requirementId,
                    requirementRevisionId: revision.id,
                    parentId: lastHistory && lastHistory.nextLevelId ? lastHistory.id : null,
                    prevUserId: prevUserId || null,
                    prevLevelId: selectedLevelId,
                    nextUserId: selectedUserId,
                    workflow: WORKFLOW_LEVEL_ASSIGNED,
                    systemMessage: true,
                    editorId: userData.userId,
                })
            }
        })

        promiseArray.push(
            UpdateCcRequirementVerifier(stateArray),
            CreateManyCcRequirementHistory(historyArray),
        )

        setSaveState(PENDING)
        Promise.all(promiseArray).then(() => {
            dispatch(setCcRequirementSelectedRequirementIds([]))
            setSelectedLevelId(null)
            setSelectedUserId(null)
            setSaveState(IDLE)

            // reload revision data
            const promiseReloadArray: Promise<any>[] = []
            Object.values(requirementList).forEach((requirement) => {
                const revision = revisionActiveList[requirement.id]
                promiseReloadArray.push(
                    LoadCcRequirementRevisionData(requirement.id, revision.id, dispatch),
                )
            })

            dispatch(setCcRequirementReviewListLoadState(PENDING))
            Promise.all(promiseReloadArray).then(() => {
                dispatch(setCcRequirementReviewListLoadState(IDLE))
            })
        })
    }

    const selectedLevelName = selectedLevelId
        ? levelList[selectedLevelId].name
        : lang.selectLevel

    const groupId = selectedLevelId
        ? levelList[selectedLevelId].groupId
        : null

    const userIds = groupId
        ? groupUserList[groupId] || []
        : []

    return (
        <>
            <InputSelectAll title={`${lang.assign}:`} />

            <SelectDropdown
                title={selectedLevelName}
                row="true"
            >
                {
                    levelIds && levelIds.map((levelId) => {
                        const level = levelList[levelId]
                        return (
                            <SelectDropdownItem
                                key={levelId}
                                onClick={() => changeLevel(levelId)}
                            >
                                {level.name}
                            </SelectDropdownItem>
                        )
                    })
                }
            </SelectDropdown>

            <SelectDropdown
                row="true"
                title={selectedUserId
                    ? <UserIcon user={userList[selectedUserId]} size="sm" />
                    : lang.selectUser}
            >
                {
                    userIds && userIds.map((userId) => {
                        const user = userList[userId]
                        return (
                            <SelectDropdownItem
                                key={userId}
                                onClick={() => setSelectedUserId(userId)}
                            >
                                <UserIcon user={user} size="sm" />
                            </SelectDropdownItem>
                        )
                    })
                }
                {
                    userIds && userIds.length === 0
                    && <SelectDropdownItem><Empty /></SelectDropdownItem>
                }
            </SelectDropdown>

            <Button
                variant="success"
                disabled={visibleSelected.length === 0 || selectedLevelId === null || selectedUserId === null}
                onClick={() => saveAssigned()}
            >
                {saveState === PENDING ? <SpinnerIcon /> : lang.assign}
            </Button>
        </>
    )
}
export default Bottom
